import {Card, Page, Tabs,Spinner,Toast,ContextualSaveBar, Modal} from '@shopify/polaris';
import React, {useCallback, useEffect, useState} from 'react';
import AppConfiguration from "./AppConfiguration";
import LocationDesignSettings from "./LocationDesignSettings";
import ExtraConfig from "./ExtraConfig";
import StoreIntegration from "./StoreIntegration";
import {useDispatch,useSelector} from "react-redux";
import {useFormik} from "formik";
import {ApiCall, GetApiCall} from "../../helper/axios";
import {updateModalStatus} from "../../redux/slice/commonSlice";
import {currencyListOptions} from "../../helper/currencyList";
import ReviewPromptModal from "../Review/ReviewPromptModal";
import AnalyticsDashboard from "../Analytics/AnalyticsDashboard";
import PromoBanner from "./PromoBanner";
import SetupIssueBanner from "./SetupIssueBanner";

// "New" feature badges. Each feature records a first-seen timestamp (per browser)
// the first time the merchant loads the app; the badge then shows for 7 days.
// Bump the version to re-announce future releases.
const NEW_FEATURES_KEY = "hw_features_seen_v2";
const NEW_BADGE_MS = 7 * 24 * 60 * 60 * 1000; // show "New" for 7 days
const readFeatureSeen = () => {
    try { return JSON.parse(window.localStorage.getItem(NEW_FEATURES_KEY) || "{}") || {}; }
    catch (e) { return {}; }
};
const ensureFeatureSeen = (keys) => {
    try {
        const seen = readFeatureSeen();
        let changed = false;
        const now = Date.now();
        keys.forEach((k) => { if (!seen[k]) { seen[k] = now; changed = true; } });
        if (changed) window.localStorage.setItem(NEW_FEATURES_KEY, JSON.stringify(seen));
        return seen;
    } catch (e) { return {}; }
};
const isFeatureNew = (seen, key) => {
    const t = seen && seen[key];
    if (!t) return true;
    return Date.now() - t < NEW_BADGE_MS;
};

// Short explanations shown in the "New" tooltips so existing merchants aren't confused.
const ANALYTICS_INFO = "See how shoppers use currencies — switches, price views, top currencies, sales by currency, and daily trends.";
const DESIGN_INFO = "Customize how the currency switcher looks, and auto-match it to your store's theme.";

// Small info icon with a NATIVE title tooltip. We must NOT use a Polaris
// <Tooltip> here: it injects a focusable wrapper, and the tab renders this
// inside a <button>, producing invalid HTML and a stray tab stop that breaks
// tab keyboard navigation. A title attribute gives the hover hint with nothing
// focusable injected.
const InfoDot = ({ content }) => (
    <span role="img" aria-label={content} title={content}
        style={{ display: "inline-flex", alignItems: "center", cursor: "help", color: "#8a8a8a" }}>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10 1.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zm0 15.5a7 7 0 110-14 7 7 0 010 14zM9 9h2v5H9V9zm0-3h2v2H9V6z" />
        </svg>
    </span>
);

// Tab label with a black "New" badge and an info tooltip beside it.
const NewTabLabel = ({ label, info }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {label}
        <span style={{ background: "#000", color: "#fff", fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 8, lineHeight: 1.6 }}>New</span>
        <InfoDot content={info} />
    </span>
);

function HomePageComponent() {
    const {status, store_data, activeModal} = useSelector((store) => store.commonData);
    const {token} = store_data;
    const isExistingCustomer = !!store_data?.isExistingCustomer; // only existing customers see "New" badges
    const showPromoBanner = store_data?.showPromoBanner !== false; // server flag; hide per store via DB
    const dispatch = useDispatch();
    const [handleChanges , setHandleChanges] = useState(false);
    const [saveLoader ,setSaveLoader] = useState(false);
    const [onLoadLoading, setOnLoadLoading] = useState(true);
    const [activeToast, setActiveToast] = useState(false);
    const [activeToastMessage, setActiveToastMessage] = useState('');
    const [reviewOpenSignal, setReviewOpenSignal] = useState(0); // bumped to open the review modal on demand
    const [reviewPresetRating, setReviewPresetRating] = useState(0); // rating chosen in the banner (1-4 -> feedback)
    const [reviewRefresh, setReviewRefresh] = useState(0); // bumped after feedback/redirect so the banner re-checks
    const [setupIssue, setSetupIssue] = useState(null); // {currencyValid, appEmbedValid} when a completed setup later breaks
    const [seenFeatures] = useState(() => ensureFeatureSeen(["analytics", "design", "themeAdaptation"])); // records first-seen; badges show for 7 days
    const toggleActive = () => {setActiveToast((activeToast) => !activeToast)};
    const toastMarkup = activeToast ? (
        <Toast content={activeToastMessage} onDismiss={toggleActive} />
    ) : null;

    const [selected, setSelected] = useState(0);
    const tabs = [
        {
            id: 'analytics-tab',
            content: (isExistingCustomer && isFeatureNew(seenFeatures, 'analytics'))
                ? <NewTabLabel label="Analytics" info={ANALYTICS_INFO} /> : 'Analytics',
            accessibilityLabel: 'Analytics',
            panelID: 'analytics-content',
            route: '/analytics',
        },
        {
            id: 'app-details-tab',
            content: 'General',
            panelID: 'app-details-content',
            route: '/',
        },
        {
            id: 'design-switcher-tab',
            content: (isExistingCustomer && isFeatureNew(seenFeatures, 'design'))
                ? <NewTabLabel label="Design" info={DESIGN_INFO} /> : 'Design',
            accessibilityLabel: 'Design',
            panelID: 'design-switcher-content',
            route: '/design-switcher',
        },
        {
            id: 'advanced-settings-tab',
            content: 'Advanced',
            panelID: 'advanced-settings-content',
            route: '/advanced-settings',
        },
        {
            id: 'store-integration-tab',
            content: 'Store Integration',
            panelID: 'store-integration-content',
            route: '/store-integration',
        },
    ];

    const handleTabChange = useCallback(
        (selectedTabIndex) => setSelected(selectedTabIndex),
        [],
    );

    // Onboarding hands control back here via these callbacks: close the takeover
    // and land the merchant on the chosen tab (Analytics by default, or Design
    // for "Customize currency switcher").
    const completeOnboarding = useCallback((tab = 0) => {
        dispatch(updateModalStatus(false));
        setSelected(tab);
    }, [dispatch]);


    const [initialState, setInitialState] = useState({
        appStatus: true,
        currenciesList: [],
        defaultCurrency: {},
        priceFormat: {label: 'With Currency', value: 0},
        displayFlag: {label: 'Yes', value: 0},
        flagStyle: {label: 'Rectangle', value: 0},
        displayBorder: {label: 'Yes', value: 0},
        borderLineColor: '#cccccc',
        textColor: '#000000',
        textHoverColor: '#000000',
        backgroundColor: '#f4f4f4',
        hoverBackgroundColor: '#f4f4f4',
        switcherLocation: {label: 'Fixed Position', value: 0},
        selectFixedPosition: 3,
        ipBasedLocation: true,
        removeDecimals: false,
        pricingRound: false,
        showCartNotice: true,
        defaultCartNoticeMessage: '<<shop.name>> process all orders in <<shop.currency>>. While the content of your cart is currently displayed in <<shop.selected_currency>>, you will checkout using <<shop.currency>> at the most current exchange rate.',
        cartNoticeMessage: '<<shop.name>> process all orders in <<shop.currency>>. While the content of your cart is currently displayed in <<shop.selected_currency>>, you will checkout using <<shop.currency>> at the most current exchange rate.',
        cartNoticetextColor: '#ffffff',
        cartNoticetextBackgroundColor: '#333333',
        showActualPriceOnHover : false,
        customcss : '',
        showDirhamSymbol: false,
        marketCompatibileMode: false,
        // Theme adaptation
        autoThemeAdaptation: true,
        borderRadius: '8px',
        fontSize: '',
        // Advanced Currency Rules
        countryOverrides: {},
        customFormats: {},
    });

    const formik = useFormik({
        initialValues: initialState,
        enableReinitialize: true,
        onSubmit: () => {
            // onClickSaveBtnFn()
        }
    });

    const saveDataShopifyFn = async () => {
        setSaveLoader(true);
        let data = {...formik.values};
        data = deleteObjectFromObj(data)

        const enableDisableAppStatus = await ApiCall('POST', '/save-data-shopify', data, { authentication: token });
        if(enableDisableAppStatus?.data?.statusCode === 200 && enableDisableAppStatus?.data?.data){
            setInitialState(prevState => ({
                ...prevState,
                ...enableDisableAppStatus?.data?.data
            }));
            setActiveToast(true)
            setActiveToastMessage("Save successfully!");
        }else{
            setActiveToast(true)
            setActiveToastMessage("Something went wrong!");
        }
        setHandleChanges(false);
        setSaveLoader(false);
    }

    let deleteObjectFromObj = (data) => {
        delete data?.appStatusLoader;
        delete data?.saveLoader;
        delete data?.designSettingsLoader;
        delete data?.locationSettingLoader;
        delete data?.cartNoticeLoader;
        delete data?.pricingRuleLoader;

        let currenciesList = data.currenciesList || [];
        let defaultCurrency = currenciesList.find(currecny => currecny.value === formik.values.storeDefaultCurrency.value);
        if(!defaultCurrency){
            currenciesList.push(formik.values.storeDefaultCurrency)
        }
        formik.setFieldValue('currenciesList',currenciesList);

        return data
    }

    useEffect(() => {
        if(token){
            onLoadFn()
            checkValidIntegration()
        }
    },[token])


    const checkValidIntegration = useCallback(async () => {
        if (!token) return;
        const res = await ApiCall('POST', '/validate-integration', {}, { authentication: token });
        const d = res?.data?.data;
        const obj = d && typeof d === 'object';
        const valid = obj ? d.valid : d;
        // "completed" = onboarding finished before, OR a pre-feature existing
        // customer. Those merchants never get the full takeover again - only the
        // red "setup broken" banner.
        const completed = (obj ? !!d.onboardingCompleted : !!valid) || isExistingCustomer;
        if (valid) {
            dispatch(updateModalStatus(false));
            setSetupIssue(null);
        } else if (!completed) {
            // First install, onboarding never finished -> full takeover.
            dispatch(updateModalStatus(true));
            setSetupIssue(null);
        } else {
            // Was set up before, now broken -> red banner instead of the takeover.
            dispatch(updateModalStatus(false));
            setSetupIssue({ currencyValid: !!d?.currencyValid, appEmbedValid: !!d?.appEmbedValid });
        }
    }, [token, dispatch, isExistingCustomer]);

    // Re-check when the merchant returns to the app, so the red banner clears
    // once they re-enable the embed (or update formatting).
    useEffect(() => {
        const onFocus = () => {
            if (!token || !setupIssue) return;
            if (document.visibilityState && document.visibilityState !== 'visible') return;
            checkValidIntegration();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [token, setupIssue, checkValidIntegration]);

    const onLoadFn = async () => {
        try {
            setOnLoadLoading(true);
            const response = await GetApiCall('GET', '/get-app-data', { authentication: token });
            if (response?.data?.statusCode === 200) {
                const metafieldData = response?.data?.data;
                if(metafieldData){
                    setInitialState(prevState => ({
                        ...prevState,
                        ...metafieldData
                    }));
                }
            } else {
                setOnLoadLoading(false);
            }
        } catch (error) {
            console.log(error, 'error')
            setOnLoadLoading(false);
        } finally {
            setOnLoadLoading(false);
        }
    };

    const changeTextFn = (field, value) => {
        let valueSet = value;
        if(field === 'currenciesList'){
            valueSet = currencyListOptions.filter(currency => value.includes(currency.value));
        }
        formik.setFieldValue([field], valueSet)
        setHandleChanges(true);
    }

    console.log(formik.values)

    const discardActionFn = () => {
        formik.resetForm();
        setHandleChanges(false);
    }

    return (
        <Page>
            <style>{`.Polaris-Tabs__Tab .Polaris-Badge,.Polaris-Tabs .Polaris-Badge{background-color:#000 !important;color:#fff !important}.Polaris-Tabs__Tab .Polaris-Badge *,.Polaris-Tabs .Polaris-Badge *{color:#fff !important}`}</style>

            {activeModal ?
                    <StoreIntegration modal={true} onComplete={() => completeOnboarding(0)} onCustomize={() => completeOnboarding(2)} />
                    : <>
                    <PromoBanner show={showPromoBanner} />
                    {setupIssue ? <SetupIssueBanner status={setupIssue} /> : null}
                    {handleChanges ?
                        <ContextualSaveBar
                            alignContentFlush
                            message="Unsaved changes"
                            saveAction={{
                                onAction: saveDataShopifyFn,
                                loading: saveLoader,
                            }}
                            discardAction={{
                                onAction: discardActionFn,
                            }}
                        />
                        : '' }

                    <Card padding="100">
                        <Tabs
                            tabs={tabs}
                            selected={selected}
                            onSelect={handleTabChange}
                            fitted
                        />
                    </Card>


                    { !onLoadLoading ?
                        <>
                            {
                                selected === 0 ? <AnalyticsDashboard embedded reviewRefresh={reviewRefresh} onOpenFeedback={(rating) => { setReviewPresetRating(rating); setReviewOpenSignal((s) => s + 1); }} /> :
                                    selected === 1 ?  <AppConfiguration formik={formik} changeTextFn={changeTextFn} /> :
                                        selected === 2 ? <LocationDesignSettings formik={formik} changeTextFn={changeTextFn} /> :
                                            selected === 3 ? <ExtraConfig formik={formik} changeTextFn={changeTextFn} /> :
                                                selected === 4 ? <StoreIntegration onComplete={() => completeOnboarding(0)} onCustomize={() => completeOnboarding(2)} /> : <p>Tab {selected} selected</p>
                            }
                        </>
                        :
                        <div style={{textAlign: "center", paddingTop: "20%"}}>
                            <Spinner accessibilityLabel="Spinner example" size="large"/>
                        </div>
                    }
                </>}

            {/* In-app review prompt (eligibility + anti-spam enforced server-side).
                Never shown during onboarding (when the integration modal is up). */}
            {!activeModal ? <ReviewPromptModal token={token} openSignal={reviewOpenSignal} presetRating={reviewPresetRating} onStateChange={() => setReviewRefresh((r) => r + 1)} /> : null}

            {toastMarkup}
        </Page>
    );
}

export default HomePageComponent;
